import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Loader2, Building2, Calendar, FileText, Euro, User, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUT_LABELS = {
  acquitte: { label: 'ACQUITTE', color: 'bg-emerald-100 text-emerald-700' },
  partiellement_paye: { label: 'PARTIELLEMENT PAYE', color: 'bg-amber-100 text-amber-700' },
  en_attente: { label: 'EN ATTENTE', color: 'bg-slate-100 text-slate-600' },
  en_retard: { label: 'EN RETARD', color: 'bg-red-100 text-red-700' },
  paye: { label: 'PAYE', color: 'bg-emerald-100 text-emerald-700' },
};

const DOC_LABELS = {
  attestation_rcds: 'Attestation RCDS',
  conditions_generales: 'Conditions Generales',
  conditions_particulieres: 'Conditions Particulieres',
  attestation_dos: 'Attestation DOS-DOMIUM',
};

function StatutBadge({ statut }) {
  const s = STATUT_LABELS[statut] || STATUT_LABELS.en_attente;
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.color}`}>{s.label}</span>;
}

function InfoRow({ icon: Icon, label, value, valueClass = '' }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-3 p-3 bg-slate-50/80 rounded-lg" data-testid={`info-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className="h-4.5 w-4.5 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</p>
        <p className={`text-sm font-semibold text-slate-800 break-words ${valueClass}`}>{value}</p>
      </div>
    </div>
  );
}

export default function VerifyRecuESS() {
  const { recuId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showSouscriptions, setShowSouscriptions] = useState(false);

  useEffect(() => {
    if (!recuId) { setError("Aucun identifiant fourni"); setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/api/verifier/recu-ess/${recuId}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Document non trouve' : 'Erreur de verification');
      } finally { setLoading(false); }
    })();
  }, [recuId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 text-[#0E6B4E] animate-spin mx-auto" />
          <p className="text-slate-600 font-medium">Verification en cours...</p>
        </div>
      </div>
    );
  }

  if (error || !data || !data.valide) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center space-y-4" data-testid="verification-error">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
            <XCircle className="h-10 w-10 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-800">Document non trouve</h1>
          <p className="text-slate-500 text-sm">{error || data?.message || "Ce document est introuvable dans notre systeme."}</p>
          <p className="text-xs text-slate-400 mt-4">Ref: {recuId}</p>
          <p className="text-xs text-slate-400">{data?.horodatage_verification || new Date().toLocaleDateString('fr-FR')}</p>
        </div>
      </div>
    );
  }

  const { recu, signataire, beneficiaire, echeancier, souscriptions_liees } = data;
  const isAcquitte = recu.statut_paiement === 'acquitte';
  
  // Determiner la mention paiement
  const mentionPaiement = isAcquitte
    ? 'Acquittee'
    : recu.statut_paiement === 'suspendue' || recu.statut_paiement === 'impayee'
      ? 'Attestation suspendue'
      : recu.montant_paye > 0 || (echeancier && !echeancier.echeances?.some(e => e.statut === 'en_retard'))
        ? 'Echeancier a jour'
        : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center p-4 py-8">
      <div className="max-w-lg w-full space-y-4" data-testid="verification-portal">

        {/* Header - Authenticite */}
        <div className={`rounded-2xl shadow-xl overflow-hidden ${isAcquitte ? 'ring-2 ring-emerald-200' : 'ring-2 ring-amber-200'}`}>
          <div className={`px-6 py-5 ${isAcquitte ? 'bg-gradient-to-r from-[#0E6B4E] to-emerald-600' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <CheckCircle className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white" data-testid="verification-status">Document Authentique</h1>
                <p className="text-white/80 text-sm">Ce recu ESS est verifie et certifie par notre systeme</p>
              </div>
            </div>
          </div>

          {/* Informations du recu */}
          <div className="bg-white p-5 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-[#0E6B4E]" />
                <span className="text-xs font-semibold text-[#0E6B4E] uppercase tracking-wider">Recu de Contribution Solidaire ESS</span>
              </div>
              <StatutBadge statut={recu.statut_paiement} />
            </div>

            <InfoRow icon={FileText} label="Numero de recu" value={recu.numero_recu} />
            <InfoRow icon={Calendar} label="Date d'emission" value={recu.date_emission ? new Date(recu.date_emission).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : ''} />
            <InfoRow icon={Euro} label="Montant global" value={`${parseFloat(recu.montant_global || recu.montant_ttc || 0).toFixed(2)} EUR`} valueClass="text-lg" />
            {recu.mode_paiement && <InfoRow icon={FileText} label="Mode de paiement" value={recu.mode_paiement} />}
            {recu.type_dispositif && <InfoRow icon={Shield} label="Type de dispositif" value={recu.type_dispositif} />}
            {recu.date_signature && <InfoRow icon={Calendar} label="Date de signature" value={new Date(recu.date_signature).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })} />}
            {recu.date_echeance && <InfoRow icon={Calendar} label="Date d'echeance" value={new Date(recu.date_echeance).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })} />}

            {/* Mention paiement */}
            {mentionPaiement && (
              <div className={`flex items-center gap-3 p-3 rounded-lg border ${
                mentionPaiement === 'Acquittee' ? 'bg-emerald-50 border-emerald-200' :
                mentionPaiement === 'Attestation suspendue' ? 'bg-red-50 border-red-200' :
                'bg-blue-50 border-blue-200'
              }`} data-testid="mention-paiement">
                {mentionPaiement === 'Acquittee' ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0" />
                ) : mentionPaiement === 'Attestation suspendue' ? (
                  <XCircle className="h-5 w-5 text-red-500 shrink-0" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-blue-500 shrink-0" />
                )}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Mention paiement</p>
                  <p className={`text-sm font-bold ${
                    mentionPaiement === 'Acquittee' ? 'text-emerald-700' :
                    mentionPaiement === 'Attestation suspendue' ? 'text-red-600' :
                    'text-blue-700'
                  }`}>{mentionPaiement}</p>
                </div>
              </div>
            )}

            {/* Reste a payer */}
            {recu.reste_a_payer > 0.01 && (
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200" data-testid="reste-a-payer">
                <Euro className="h-4.5 w-4.5 text-red-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-red-400 font-medium">Reste a payer</p>
                  <p className="text-sm font-bold text-red-600">{parseFloat(recu.reste_a_payer).toFixed(2)} EUR</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Signataire */}
        <div className="bg-white rounded-2xl shadow-lg p-5 space-y-3" data-testid="signataire-section">
          <div className="flex items-center gap-2 mb-1">
            <User className="h-4 w-4 text-[#C9A24D]" />
            <span className="text-xs font-semibold text-[#C9A24D] uppercase tracking-wider">Signataire</span>
          </div>
          <div className="p-3 bg-gradient-to-r from-slate-50 to-stone-50 rounded-lg border border-slate-200">
            <p className="text-base font-bold text-slate-800" data-testid="signataire-nom">{signataire.genre} {signataire.nom}</p>
            <p className="text-sm text-slate-500 italic">{signataire.titre}</p>
            <p className="text-xs text-slate-400 mt-1">{signataire.organisation}</p>
          </div>
        </div>

        {/* Beneficiaire */}
        <div className="bg-white rounded-2xl shadow-lg p-5 space-y-3" data-testid="beneficiaire-section">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="h-4 w-4 text-[#0E6B4E]" />
            <span className="text-xs font-semibold text-[#0E6B4E] uppercase tracking-wider">Beneficiaire</span>
          </div>
          <InfoRow icon={Building2} label="Raison sociale" value={beneficiaire.raison_sociale} />
          {beneficiaire.siret && <InfoRow icon={FileText} label="SIRET" value={beneficiaire.siret} />}
          {beneficiaire.ville && (
            <InfoRow icon={Building2} label="Localisation" value={[beneficiaire.code_postal, beneficiaire.ville].filter(Boolean).join(' ')} />
          )}
        </div>

        {/* Echeancier */}
        {echeancier && (
          <div className="bg-white rounded-2xl shadow-lg p-5 space-y-3" data-testid="echeancier-section">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Echeancier ({echeancier.nb_echeances} echeances)</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <p className="text-[10px] text-emerald-500 uppercase">Paye</p>
                <p className="text-sm font-bold text-emerald-700">{parseFloat(echeancier.total_paye).toFixed(2)} EUR</p>
              </div>
              <div className="p-2 bg-red-50 rounded-lg">
                <p className="text-[10px] text-red-500 uppercase">Reste</p>
                <p className="text-sm font-bold text-red-600">{parseFloat(echeancier.reste_a_payer).toFixed(2)} EUR</p>
              </div>
            </div>
            <div className="space-y-1.5 mt-2">
              {echeancier.echeances.map((e, i) => (
                <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${e.statut === 'paye' ? 'bg-emerald-400' : e.statut === 'en_retard' ? 'bg-red-400' : 'bg-amber-300'}`} />
                    <span className="font-medium text-slate-700">Echeance #{e.numero}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500">{parseFloat(e.montant).toFixed(2)} EUR</span>
                    <StatutBadge statut={e.statut} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Souscriptions liees */}
        {souscriptions_liees && souscriptions_liees.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-5 space-y-3" data-testid="souscriptions-section">
            <button
              onClick={() => setShowSouscriptions(!showSouscriptions)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-indigo-500" />
                <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">
                  Souscriptions liees ({souscriptions_liees.length})
                </span>
              </div>
              {showSouscriptions ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </button>
            {showSouscriptions && (
              <div className="space-y-2 mt-2">
                {souscriptions_liees.map((s, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-1.5" data-testid={`souscription-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-700">{s.type}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        s.paiement_valide ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                      }`}>
                        {s.statut?.toUpperCase() || 'N/A'}
                      </span>
                    </div>
                    {s.numero_police && <p className="text-[10px] text-slate-400">Police: {s.numero_police}</p>}
                    {s.documents_disponibles && s.documents_disponibles.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {s.documents_disponibles.map((doc, j) => (
                          <span key={j} className="text-[9px] bg-white border border-slate-200 text-slate-500 px-1.5 py-0.5 rounded">
                            {DOC_LABELS[doc] || doc}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center space-y-1 pt-2 pb-4">
          <p className="text-[10px] text-slate-400">
            Verification effectuee le {data.horodatage_verification}
          </p>
          <p className="text-[10px] text-slate-400">Ref: {recuId}</p>
          <p className="text-[10px] text-slate-300 mt-2">COPPAM / O'SCOP Outremer - Portail de verification</p>
        </div>
      </div>
    </div>
  );
}
